<template>
  <!-- Sidebar wrapper -->
  <div
    class="bg-vice-light my-2 rounded-2xl relative transition-all duration-300 overflow-hidden"
    :class="{
      'w-sidebar mx-2 min-w-sidebar': sessionStore.isOpenSidebar,
      'min-w-0 w-0': !sessionStore.isOpenSidebar,
    }"
  >
    <!-- Sidebar content -->
    <div class="absolute w-sidebar flex flex-col h-full">
      <!-- Tabs -->
      <nav class="relative z-0 flex w-full">
        <!-- Chat Tab Link-->
        <button
          data-cy="sidebar-chat-tab"
          @click="setActiveTab('chatTab')"
          class="
            step-chat-tab
            text-gray-500
            group
            relative
            flex-1
            justify-center
            flex
            overflow-hidden
            py-4
            px-2
            text-center
            border-l
          "
          :class="{ 'text-vice-teal-900 ': activeTab === 'chatTab' }"
        >
          <div class="flex flex- justify-center items-center relative">
            <ChatAlt2Icon class="h-5 w-5 mr-1" />
            <span
              class="text-xxs font-light text-gray-500"
              :class="{ 'text-gray-900 font-bold': activeTab === 'chatTab' }"
            >
              CHAT
            </span>
            <!-- Unread messages badge -->
            <span
              v-if="
                chatMessagesStore.hasUnreadMessages &&
                (activeTab !== 'chatTab' || !sessionStore.isOpenSidebar)
              "
              class="absolute -top-0.5 -left-0.5 w-2 h-2 bg-red-500 rounded-full"
            ></span>
          </div>
          <span
            aria-hidden="true"
            class="absolute inset-x-0 bottom-0 h-0.5"
            :class="activeTab === 'chatTab' ? 'bg-vice-teal-400' : 'bg-gray-300'"
          ></span>
        </button>

        <!-- Attendees Tab Link-->
        <button
          data-cy="sidebar-attendees-tab"
          @click="setActiveTab('participantsTab')"
          class="
            step-attendees-tab
            text-gray-500
            group
            relative
            flex-1
            justify-center
            items-center
            flex
            overflow-hidden
            py-4
            px-3
            text-center
            shadow-inner-lg
            border-l
          "
          :class="{ 'text-vice-teal-900 ': activeTab === 'participantsTab' }"
        >
          <div class="flex flex- justify-center items-center">
            <UsersIcon class="h-5 w-5 mr-1" />
            <div class="flex items-center text-xs">
              <span
                class="text-xxs font-light text-gray-500"
                :class="{ 'text-gray-900 font-bold': activeTab === 'participantsTab' }"
              >
                ATTENDEES
              </span>
              <div
                class="leading-tight rounded-xl px-1 bg-gray-100 text-gray-500"
                :class="{ 'text-gray-900 font-bold': activeTab === 'participantsTab' }"
              >
                {{ usersPresenceStore.totalPresentUsers }}
              </div>
            </div>
          </div>
          <span
            aria-hidden="true"
            class="absolute inset-x-0 bottom-0 h-0.5"
            :class="activeTab === 'participantsTab' ? 'bg-vice-teal-400' : 'bg-gray-300'"
          ></span>
        </button>

        <!-- Session Tab Link -->
        <button
          v-if="showSessionTab"
          data-cy="sidebar-session-tab"
          @click="setActiveTab('sessionTab')"
          class="
            step-chat-tab
            text-gray-500
            group
            relative
            flex-1
            justify-center
            flex
            overflow-hidden
            py-4
            px-2
            text-center
            border-l
          "
          :class="{ 'text-vice-teal-900 ': activeTab === 'sessionTab' }"
        >
          <div class="flex flex- justify-center items-center">
            <SessionInfoIcon class="h-4 w-4 mr-1" />
            <span
              class="text-xxs font-light text-gray-500"
              :class="{ 'text-gray-900 font-bold': activeTab === 'sessionTab' }"
            >
              SNAPSHOTS
            </span>
          </div>
          <span
            aria-hidden="true"
            class="absolute inset-x-0 bottom-0 h-0.5"
            :class="activeTab === 'sessionTab' ? 'bg-vice-teal-400' : 'bg-gray-300'"
          ></span>
        </button>

        <!-- Reflections Tab -->
        <button
          v-if="!sessionStore.isClinician"
          data-cy="sidebar-reflection-tab"
          @click="setActiveTab('reflectionTab')"
          class="
            step-chat-tab
            text-gray-500
            group
            relative
            flex-1
            justify-center
            flex
            overflow-hidden
            py-4
            px-2
            text-center
            border-l
          "
          :class="{ 'text-vice-teal-900 ': activeTab === 'reflectionTab' }"
        >
          <div class="flex flex- justify-center items-center">
            <SessionInfoIcon class="h-4 w-4 mr-1" />
            <span
              class="text-xxs font-light text-gray-500"
              :class="{ 'text-gray-900 font-bold': activeTab === 'reflectionTab' }"
            >
              REFLECTION
            </span>
          </div>
          <span
            aria-hidden="true"
            class="absolute inset-x-0 bottom-0 h-0.5"
            :class="activeTab === 'reflectionTab' ? 'bg-vice-teal-400' : 'bg-gray-300'"
          ></span>
        </button>
      </nav>

      <!-- Chat tab -->
      <ChatLayout
        v-show="activeTab === 'chatTab'"
        :clinicianName="clinicianName"
        :participants-list="participantsList"
        :userIsObserver="sessionStore.userIsObserver"
      />

      <!-- Participants tab -->
      <ParticipantsList
        v-show="activeTab === 'participantsTab'"
        :participants-list="participantsList"
        :latecomers-list="sessionStore.isClinician ? latecomersList : []"
        :muteSingleParticipant="muteSingleParticipant"
        :kickParticipant="kickParticipant"
        @lateEntryResponse="lateEntryResponse"
        @lowerHand="$emit('lowerHand', $event)"
      />

      <!-- Session tab -->
      <SessionLayout v-show="activeTab === 'sessionTab'" :clinicianName="clinicianName" />

      <!-- Reflection tab -->
      <ReflectionLayout v-show="activeTab === 'reflectionTab'" />
    </div>
    <!-- End of sidebar content -->

    <!-- Toasts Notifications (Clinician) -->
    <template v-if="sessionStore.isClinician">
      <LateEntryToast
        v-for="(latecomer, index) in latecomersToasts"
        @audioNotification="playAudioNotification('late_entry')"
        @lateEntryResponse="lateEntryResponse"
        :toggleSidebar="sessionStore.toggleSidebar"
        :clearToastStack="clearLatecomersStack"
        :latecomer="latecomer"
        :index="index"
        :key="index"
      />

      <StethoscopeConnectionToast
        v-if="false"
        @audioNotification="playAudioNotification('stethoscope_connected')"
        @toggleToast="toggleToast('stethoscopeToast')"
        @stethoscopeConnectionAllowed="connectionAccepted"
        :latecomersAmount="latecomersToasts ? latecomersToasts.length : 0"
      />
    </template>
    <!-- Toasts Notifications (Observers) -->
    <template v-if="sessionStore.userIsObserver">
      <ObserverInfoToast />
    </template>
    <!-- End of sidebar wrapper -->
  </div>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3';
import { computed, onMounted, ref, watch } from 'vue';
import { ChatAlt2Icon, UsersIcon } from '@heroicons/vue/solid';
import ChatLayout from '@/Components/Chat/ChatLayout.vue';
import PhaseService from '@/Services/PhaseService';
import useSessionStore from '@/Stores/useSessionStore';
import useIOTDevicesStore from '@/Stores/useIOTDevicesStore';
import useUsersPresenceStore from '@/Stores/useUsersPresenceStore';
import useChatMessagesStore from '@/Stores/useChatMessagesStore';
import LateEntryToast from '@/Components/Toasts/LateEntryToast.vue';
import ParticipantsList from '@/Components/Chat/ParticipantsList.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import StethoscopeConnectionToast from '@/Components/Toasts/StethoscopeConnectionToast.vue';
import {
  EXTERNAL_CONNECTED_AUDIO_DEVICES,
  STETHOSCOPE_LABELS,
  USER_ROLE,
  VICE_SESSION_PHASES,
} from '@/Utils/vice-constants';
import SessionInfoIcon from '@/Components/Icons/SessionInfoIcon.vue';
import SessionLayout from '@/Components/Chat/SessionLayout.vue';
import ReflectionLayout from './ReflectionLayout.vue';
import ObserverInfoToast from '@/Components/Toasts/ObserverInfoToast.vue';

export default {
  components: {
    ObserverInfoToast,
    SessionLayout,
    ReflectionLayout,
    SessionInfoIcon,
    UsersIcon,
    ChatLayout,
    ChatAlt2Icon,
    LateEntryToast,
    ParticipantsList,
    StethoscopeConnectionToast,
  },
  emits: ['lowerHand'],
  props: {
    muteSingleParticipant: {
      type: Function,
    },
    kickParticipant: {
      type: Function,
    },
  },
  setup() {
    const latecomersList = ref([]);
    const pageProps = usePage().props.value;
    const toasts = { stethoscopeToast: ref(false) };
    const { session } = pageProps;

    const sessionStore = useSessionStore();
    const streamingDeviceStore = useIOTDevicesStore();
    const usersPresenceStore = useUsersPresenceStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();
    const chatMessagesStore = useChatMessagesStore();

    const activeTab = computed(() => sessionStore.sidebarTab);

    const latecomersToasts = computed(() => latecomersList.value.filter((user) => user.showToast));

    const showSessionTab = computed(
      () =>
        !!sessionStore.isClinician &&
        (sessionStore.snapshotsAllowed || sessionStore.videoSnapshotsAllowed),
    );
    const isStethoscopeConnected = computed(
      () =>
        !!multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.AUDIO.Microphone.find((device) =>
          STETHOSCOPE_LABELS.some((singleLabel) => device.label.includes(singleLabel)),
        ),
    );

    const isCurrentlyStreaming = computed(
      () =>
        !!multiCameraDevicesStore.allExternalConnectedAudioDevices.find(
          (deviceName) => deviceName === EXTERNAL_CONNECTED_AUDIO_DEVICES.STETHOSCOPE,
        ),
    );

    const toggleToast = (toast, toState = null) => {
      toasts[toast].value = typeof toState === 'boolean' ? toState : !toasts[toast].value;
    };

    const clearLatecomersStack = (userId = null) => {
      // If no userId - remove all toasts
      if (userId == null) {
        latecomersList.value.forEach((latecomer) => {
          latecomer.showToast = false;
        });
        return;
      }
      // Remove single toast by userId
      const latecomer = latecomersList.value.find((obj) => obj.id === userId);
      if (latecomer) {
        latecomer.showToast = false;
      }
    };

    const connectionAccepted = (accepted) => {
      if (accepted) {
        handleStethoscopeConnection();
      }
      toggleToast('stethoscopeToast', false);
    };

    const setActiveTab = (tabName) => {
      sessionStore.sidebarTab = tabName;
    };

    // Play audio notification
    const playAudioNotification = async (type) => {
      const audioSource = `/audio/toasts/${type}.mp3`;
      const audio = new Audio(audioSource);

      try {
        await audio.play();
      } catch (error) {
        console.error('Error playing audio notification:', error);
      }
    };

    const handleStethoscopeConnection = async () => {
      try {
        if (isCurrentlyStreaming.value) {
          await multiCameraDevicesStore.removeExternalAudioDevice(
            EXTERNAL_CONNECTED_AUDIO_DEVICES.STETHOSCOPE,
          );
        } else {
          await multiCameraDevicesStore.addExternalAudioDevice(
            sessionStore.sessionId,
            EXTERNAL_CONNECTED_AUDIO_DEVICES.STETHOSCOPE,
            STETHOSCOPE_LABELS,
          );
        }
        multiCameraDevicesStore.markSessionSetupAsChanged();
      } catch (error) {
        console.error({ error });
      }
    };

    watch(activeTab, (newValue) => {
      // Auto-hide late entry toast when switching to participants tab
      // if (
      //   sessionStore.isClinician &&
      //   latecomersToasts.value.length &&
      //   newValue === 'participantsTab'
      // ) {
      //   setTimeout(() => {
      //     clearLatecomersStack();
      //   }, 1000);
      // }

      // Clear unread messages count when switching to chat tab
      if (newValue === 'chatTab') {
        chatMessagesStore.clearUnreadCount();
      }
    });

    watch(
      () => sessionStore.isOpenSidebar,
      (newValue) => {
        // Auto-hide late entry toast when sidebar is just opened and participants tab is active
        // if (
        //   newValue === true &&
        //   sessionStore.isClinician &&
        //   latecomersToasts.value.length &&
        //   activeTab.value === 'participantsTab'
        // ) {
        //   setTimeout(() => {
        //     clearLatecomersStack();
        //   }, 1000);
        // }

        // Clear unread messages count when sidebar is opened and chat tab is active
        if (newValue === true && activeTab.value === 'chatTab') {
          chatMessagesStore.clearUnreadCount();
        }
      },
    );

    watch(isStethoscopeConnected, (newValue, oldValue) => {
      if (
        newValue &&
        sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION &&
        sessionStore.hasLivePhase
      ) {
        toggleToast('stethoscopeToast', true);

        setTimeout(() => {
          toggleToast('stethoscopeToast', false);
        }, 5000);
      } else if (!newValue && oldValue) {
        toggleToast('stethoscopeToast', false);

        if (isCurrentlyStreaming.value) {
          handleStethoscopeConnection();
        }
      }
    });

    const participants = session.users
      .filter((invitedUser) => invitedUser.user_type !== USER_ROLE.OBSERVER)
      .map((participant) => ({
        id: participant.id,
        name: participant.name,
        avatarUrl: participant.profile_photo_url,
      }));

    const clinicianInfo = {
      id: session.details.clinician?.id,
      name: session.details.clinician?.name,
      avatarUrl: session.details.clinician?.profile_photo_url,
    };

    const participantsList = [clinicianInfo, ...participants];

    const lateEntryResponse = (user, accepted) => {
      const data = {
        uuid: session.unique_id,
        userUuid: user.id,
        accepted,
      };

      PhaseService.lateEntryRequest(data).then(() => {
        window.Echo.private(`late.entry.${session.unique_id}`).whisper('response', data);
      });

      const index = latecomersList.value.findIndex((obj) => obj.id === user.id);
      if (index !== -1) {
        latecomersList.value.splice(index, 1);
      }
      clearLatecomersStack(user.id);
    };

    watch(
      () => sessionStore.autoAcceptLatecomers,
      (enabled) => {
        if (enabled) {
          [...latecomersList.value].forEach((user) => lateEntryResponse(user, true));
        }
      },
    );

    window.Echo.join(`presence.channel.${session.unique_id}`)
      .here((users) => {
        usersPresenceStore.initUsers(users);
      })
      .joining((joiningUser) => {
        usersPresenceStore.addUser(joiningUser);
      })
      .leaving((leavingUser) => {
        usersPresenceStore.removeUser(leavingUser);
      })
      .error((error) => {
        console.error(error);
      });

    onMounted(async () => {
      if (sessionStore.isOpenSidebar && activeTab.value === 'chatTab') {
        chatMessagesStore.clearUnreadCount();
      }
      if (session.late_entry) {
        window.Echo.private(`late.entry.${session.unique_id}`).listenForWhisper(
          'entry-request',
          (user) => {
            if (sessionStore.autoAcceptLatecomers) {
              lateEntryResponse(user, true);
              return;
            }

            const index = latecomersList.value.findIndex((obj) => obj.id === user.id);
            if (index !== -1) {
              latecomersList.value.splice(index, 1);
            }
            latecomersList.value.push(user);

            // Automatically hide the toast after 5 seconds if the participants tab is active
            // if (
            //   sessionStore.isClinician &&
            //   sessionStore.isOpenSidebar &&
            //   activeTab.value === 'participantsTab'
            // ) {
            //   setTimeout(() => {
            //     clearLatecomersStack(user.id);
            //   }, 3000);
            // }
          },
        );
      }
    });

    return {
      toasts,
      activeTab,
      sessionStore,
      showSessionTab,
      latecomersList,
      participantsList,
      latecomersToasts,
      usersPresenceStore,
      streamingDeviceStore,
      chatMessagesStore,
      playAudioNotification,
      isStethoscopeConnected,
      toggleToast,
      setActiveTab,
      lateEntryResponse,
      connectionAccepted,
      clearLatecomersStack,
      clinicianName: clinicianInfo.name,
    };
  },
};
</script>

<style scoped />
